import { Row, Stack } from "@justworkshr/milo-core";
import { DisplayIcon, DisplayIconNames } from "@justworkshr/milo-icons";
import { ReactElement, ReactNode } from "react";
import { INTERNATIONAL_CONTRACTORS_PRODUCT_HELP_LINK } from "../../constants";
import styles from "../../InternationalSolutionsPage.module.css";

type FeatureCardProps = {
  iconName: keyof typeof DisplayIconNames;
  iconTitle: string;
  title: string;
  description: ReactNode;
};

const FeatureCard = ({
  iconName,
  iconTitle,
  title,
  description,
}: FeatureCardProps) => {
  const { smHead, featureCardRow, featureCardIcon, mdFont } = styles;

  return (
    <Row spacing="lg" className={featureCardRow}>
      <DisplayIcon
        color="greenYellow"
        iconName={iconName}
        size="small"
        title={iconTitle}
        className={featureCardIcon}
      />
      <Stack spacing="xs">
        <h4 className={smHead}>{title}</h4>
        <p className={mdFont}>{description}</p>
      </Stack>
    </Row>
  );
};

const Overview = (): ReactElement => {
  const { lgHead, mdFont } = styles;

  return (
    <Stack spacing="2xl">
      <Stack spacing="sm">
        <p className={lgHead}>International contractors</p>
        <p className={mdFont}>
          Access top talent in markets like{" "}
          <b>Brazil, Colombia, India, and the Philippines</b> with a streamlined
          solution that saves time, offers currency optionality, and grows with
          your team. Available in over 60 countries and counting!
        </p>
      </Stack>
      <Stack spacing="md">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="31"
          height="27"
          viewBox="0 0 31 27"
          fill="none"
        >
          <path
            d="M11.4041 0L7.22798 12.9341H13.9741V27H0V12.9341L6.58549 0H11.4041ZM28.4301 0L24.2539 12.9341H31V27H17.0259V12.9341L23.6114 0H28.4301Z"
            fill="#0070D4"
          />
        </svg>
        <Stack spacing="md" className={mdFont}>
          <p>
            Using Justworks allows us to provide amazing job opportunities for
            people in different states and countries—and we get the benefit of a
            diverse, talented team which is a key competitive advantage for us.
          </p>
          <span>
            — <b>Jeremy Clent</b>, Co-Founder, Project Expedition
          </span>
        </Stack>
      </Stack>
      <Stack spacing="sm">
        <p className={lgHead}>A flexible, global solution</p>
        <FeatureCard
          iconName="shield"
          iconTitle="Shield Icon"
          title="Expanded access to global talent"
          description={
            <>
              Recruit top talent in over 50 countries, and we’re still adding
              more!{" "}
              <a
                href={INTERNATIONAL_CONTRACTORS_PRODUCT_HELP_LINK}
                target="_blank"
              >
                Check out the full list of supported countries
              </a>
            </>
          }
        />
        <FeatureCard
          iconName="circle-with-cross"
          iconTitle="Circle with Cross Icon"
          title="One platform, everyone together"
          description="Manage international contractors alongside your domestic and international employees without any additional tools."
        />
        <FeatureCard
          iconName="hand-waving"
          iconTitle="Hand Waving Icon"
          title="Only pay for what you use"
          description="No setup fees and no commitments. Pay contractors in USD or local currency with full visibility into exchange rates, and a single, simple invoice—only get billed for the contractors you pay each month."
        />
        <FeatureCard
          iconName="laptop-and-plus-sign"
          iconTitle="Laptop and Plus Sign Icon"
          title="Avoid delays and costly errors"
          description="Our streamlined contractor payment solution ensures accuracy across every transaction."
        />
      </Stack>
    </Stack>
  );
};

export default Overview;
